import React, { type ElementRef, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import StandardButton from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import CheckIcon from '@atlaskit/icon/glyph/check';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import messages from './messages.tsx';

interface Props {
	onConfirm: (newValue: string) => void;
	title: MessageDescriptor;
	placeholderText: MessageDescriptor;
	confirmButtonAriaLabel: MessageDescriptor;
	cancelButtonAriaLabel: MessageDescriptor;
	textAreaFocusAriaLabel: MessageDescriptor;
	editIconAriaLabel: MessageDescriptor;
	defaultValue: string;
	isReadOnlyMode?: boolean;
}

export const TextAreaInput = ({
	onConfirm,
	title,
	placeholderText,
	confirmButtonAriaLabel,
	cancelButtonAriaLabel,
	textAreaFocusAriaLabel,
	editIconAriaLabel,
	defaultValue,
	isReadOnlyMode = false,
}: Props) => {
	const { formatMessage } = useIntl();
	const [isWriteMode, setIsWriteMode] = useState<boolean>(false);
	const textAreaRef = useRef<ElementRef<'textarea'>>(null);
	const [text, setText] = useState<string>(defaultValue);

	const onEnterWriteMode = () => {
		if (isReadOnlyMode) return;

		setIsWriteMode(true);
		// In order to focus, it should do it in the next frame, as textArea is `display: none` in the current frame
		setTimeout(() => {
			if (textAreaRef.current != null) {
				textAreaRef.current.focus();
			}
		}, 0);
	};

	return (
		<Box xcss={descriptionEditableAreaWrapperStyles}>
			<HeadingWrapper>
				<HeadingUpperCase>
					<Heading size="xxsmall" as="h4">
						{formatMessage(title)}
					</Heading>
				</HeadingUpperCase>
				{text.trim() !== defaultValue.trim() && !isWriteMode && (
					<Box xcss={draftIndicatorStyles}>
						<Heading
							size="xxsmall"
							as="h4"
						>{`• ${formatMessage(messages.unsavedChanges)}`}</Heading>
					</Box>
				)}
			</HeadingWrapper>
			<EditModeWrapper isWriteMode={isWriteMode}>
				<TextArea
					defaultValue={defaultValue}
					value={text}
					ref={textAreaRef}
					onBlur={() => {
						setIsWriteMode(false);
					}}
					minimumRows={4}
					onChange={(e) => {
						setText(e.target.value);
					}}
				/>
				<ButtonWrapper>
					<StandardButton
						appearance="primary"
						onClick={() => {
							onConfirm(text.trim());
							setIsWriteMode(false);
						}}
						isDisabled={text.trim() === defaultValue.trim()}
						iconBefore={<CheckIcon label={formatMessage(confirmButtonAriaLabel)} />}
					/>
					<StandardButton
						onClick={() => {
							setIsWriteMode(false);
							setText(defaultValue);
						}}
						iconBefore={<CrossIcon label={formatMessage(cancelButtonAriaLabel)} />}
					/>
				</ButtonWrapper>
			</EditModeWrapper>
			<ReadModeWrapper isWriteMode={isWriteMode}>
				<DescriptionReadOnly
					isPlaceHolder={defaultValue === ''}
					onClick={() => {
						onEnterWriteMode();
					}}
					isReadOnlyMode={isReadOnlyMode}
				>
					<StandardButton
						aria-label={formatMessage(textAreaFocusAriaLabel)}
						appearance="subtle"
						spacing="none"
						onClick={() => {
							onEnterWriteMode();
						}}
					/>
					<ReadOnlyText>
						{defaultValue === '' ? formatMessage(placeholderText) : defaultValue}
					</ReadOnlyText>
					{!isReadOnlyMode && (
						<EditFilledIcon size="small" label={formatMessage(editIconAriaLabel)} />
					)}
				</DescriptionReadOnly>
			</ReadModeWrapper>
		</Box>
	);
};

const descriptionEditableAreaWrapperStyles = xcss({
	marginTop: 'space.050',
	width: '100%',
});

interface WrapperProps {
	isWriteMode: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditModeWrapper = styled.div<WrapperProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: `${({ isWriteMode }: WrapperProps) => (isWriteMode ? 'block' : 'none')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadModeWrapper = styled.div<WrapperProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: `${({ isWriteMode }: WrapperProps) => (isWriteMode ? 'none' : 'block')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingUpperCase = styled.div({
	textTransform: 'uppercase',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	marginTop: token('space.100', '8px'),
});

interface DescriptionReadOnlyProps {
	isPlaceHolder: boolean;
	isReadOnlyMode: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionReadOnly = styled.div<DescriptionReadOnlyProps>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: `${({ isReadOnlyMode }: DescriptionReadOnlyProps) =>
		isReadOnlyMode ? 'auto' : 'pointer'}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button:focus': {
		outline: '0px',
	},
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: `${({ isReadOnlyMode }: DescriptionReadOnlyProps) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			isReadOnlyMode ? 'transparent' : token('color.background.neutral.subtle', colors.N30A)}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			opacity: 1,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		opacity: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		flex: 0,
	},
	'&:focus-within': {
		// Do not use shortened expression as compiled-css has a bug: https://atlassian.slack.com/archives/C0256AVSPHN/p1685512354715019
		outlineWidth: '2px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		outlineColor: `${token('color.border.focused', colors.B200)}`,
		outlineStyle: 'solid',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			opacity: 1,
		},
	},
	transition: 'all ease 250ms',
	padding: token('space.050', '4px'),
	marginLeft: token('space.negative.050', '-4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	fontStyle: `${({ isPlaceHolder }: DescriptionReadOnlyProps) =>
		isPlaceHolder ? 'italic' : 'normal'}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: `${({ isPlaceHolder }: DescriptionReadOnlyProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		isPlaceHolder ? token('color.text.subtlest', colors.N300) : undefined}`,
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadOnlyText = styled.p({
	margin: 0,
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const draftIndicatorStyles = xcss({
	marginLeft: 'space.100',
});
